'use client';

import { useTranslations, useLocale } from '@fshr/context/i18n-context';
import { Alert, Button, Icon, Input, Link, PhoneNumber } from '@fshr/components';
import { cn, getTollFreeNumbersLink } from '@fshr/shared-react';
import { useForm } from '@fshr/hooks';
import { useEffect } from 'react';

export interface AdditionarRequestOtpClasses {
	nextButtonAdditionalClasses?: string;
}
interface RequestOTPProps {
	isUserPersistent: boolean; // New prop to control conditional rendering
	sendCodeViaOptions: string[];
	sendCodeVia: string;
	sendCodeTo: string;
	submittedSendCodeTo: string;
	error?: string | null;
	countries?: any;
	additionalRequestOtpClasses?: AdditionarRequestOtpClasses;
	setSendCodeVia: (str: string) => void;
	setSendCodeTo: (str: string) => void;
	onSubmit: () => void;
	handleOTPSubmit?: (formValues: { [key: string]: string }) => Promise<void>;
}

export default function RequestOTP({
	isUserPersistent,
	sendCodeViaOptions,
	sendCodeVia,
	sendCodeTo,
	submittedSendCodeTo,
	error,
	countries,
	setSendCodeVia,
	setSendCodeTo,
	onSubmit,
	handleOTPSubmit,
	additionalRequestOtpClasses = {},
}: RequestOTPProps) {
	const t = useTranslations();
	const fsLocale = useLocale();
	const { handleSubmit, registerField, setFormErrorMessage } = useForm({
		onSubmit: handleOTPSubmit || (() => Promise.resolve()),
	});

	const { nextButtonAdditionalClasses = '' } = additionalRequestOtpClasses;

	const notFoundErrors = ['E0218', 'E0219'];

	const LOGIN_TYPES = {
		PHONE_NUMBER: 'PHONE',
		EMAIL_ADDRESS: 'EMAIL',
	};

	const labels = {
		[LOGIN_TYPES.EMAIL_ADDRESS]: t('Via email'),
		[LOGIN_TYPES.PHONE_NUMBER]: t('Via text'),
	};

	useEffect(() => {
		if (error && !notFoundErrors.includes(error)) {
			if (sendCodeVia !== LOGIN_TYPES.EMAIL_ADDRESS) {
				setFormErrorMessage('phone', t('Please enter a valid phone number.'));
			} else {
				setFormErrorMessage('email', t('Please enter a valid email address.'));
			}
		}
	}, [error]);

	const handleFormSubmit = (evt: React.FormEvent<HTMLFormElement>) => {
		evt.preventDefault();
		if (handleOTPSubmit) {
			handleSubmit(evt);
		} else {
			onSubmit();
		}
	};

	return (
		<div className="w-full flex flex-col items-center text-center">
			<h1 className="mb-200 text-h2">
				{!isUserPersistent ? t('Sign In') : t('Sign In To Continue')}
			</h1>
			<p className="mb-300 text-body2">
				{!isUserPersistent
					? t('View and edit your Four Seasons profile and customise your preferences.')
					: t('Please sign in and verify your identity.')}
			</p>

			<form onSubmit={handleFormSubmit} className="w-full">
				<div
					className="mb-200 flex flex-row justify-center gap-300"
					role="group"
					aria-label={t('Sign in method')}
				>
					{sendCodeViaOptions.map(sendCodeViaOption => (
						<label
							key={sendCodeViaOption}
							className="text-body2 flex flex-row gap-100 items-center cursor-pointer"
						>
							<input
								type="radio"
								name="sendCodeViaOption"
								value={sendCodeViaOption}
								checked={sendCodeViaOption === sendCodeVia}
								onChange={evt => setSendCodeVia(evt.target.value)}
								className={cn([
									'appearance-none',
									'cursor-pointer',
									'block',
									'leading-none',
									'w-200',
									'h-200',
									'rounded-full',
									'border',
									'border-border-default',
									'checked:bg-black',
									'checked:ring-[4px]',
									'checked:ring-white',
									'checked:ring-inset',
								])}
							/>
							{labels[sendCodeViaOption]}
						</label>
					))}
				</div>

				{error && notFoundErrors.includes(error) && (
					<div className="mb-200">
						<Alert intent="error" kind="subtle" icon={<Icon icon="alert" />}>
							<span
								className="text-caption"
								dangerouslySetInnerHTML={{
									__html: t(`error.${error}`, [submittedSendCodeTo]),
								}}
							/>
						</Alert>
					</div>
				)}
				{error && !notFoundErrors.includes(error) && isUserPersistent && (
					<div className="mb-200">
						<Alert intent="error" kind="subtle" icon={<Icon icon="alert" />}>
							<span className="text-caption">
								{t(
									'There was a problem processing your request. Please contact us for assistance.'
								)}
							</span>
						</Alert>
					</div>
				)}

				<div className=" flex flex-col items-center max-w-full">
					<div
						className={cn('w-full', {
							'text-center': isUserPersistent,
							'text-left': !isUserPersistent,
						})}
					>
						{isUserPersistent ? (
							<p
								className={`text-body2 ${
									sendCodeVia === LOGIN_TYPES.PHONE_NUMBER ? 'mb-200' : 'mb-300'
								}`}
							>
								{sendCodeTo}
							</p>
						) : (
							<>
								{sendCodeVia !== LOGIN_TYPES.PHONE_NUMBER ? (
									<Input
										id="email"
										className={cn('bg-surface-form px-4')}
										label={t('Email')}
										placeholder={t('Email')}
										{...registerField({
											name: 'email',
											type: 'email',
											initialValue: '',
											options: {
												errorMessages: {
													empty: t('Email is required.'),
													partial: t('Please enter a valid email address.'),
												},
												onChange: e => setSendCodeTo(e.currentTarget.value),
												required: true,
											},
										})}
									/>
								) : (
									<>
										{countries && (
											<PhoneNumber
												id="phone"
												label={t('Phone Number')}
												{...registerField({
													name: 'phone',
													type: 'phone',
													initialValue: '',
													options: {
														errorMessages: {
															empty: t('Phone is required.'),
															partial: t('Please enter a valid phone number.'),
														},
														onChange: e => setSendCodeTo(e.currentTarget.value),
														required: true,
													},
												})}
												countries={countries}
											/>
										)}
									</>
								)}
							</>
						)}
					</div>

					{sendCodeVia === LOGIN_TYPES.PHONE_NUMBER && (
						<p className={`text-caption text-text-secondary mb-300`}>
							{t(
								'If you choose to receive your password via mobile, we will send one text message per login attempt. Message and data rates may apply.'
							)}
						</p>
					)}
					{error && !notFoundErrors.includes(error) && !isUserPersistent && (
						<div className="mb-200">
							<Alert intent="error" kind="subtle" icon={<Icon icon="alert" />}>
								<span className="text-caption">
									{t(
										'There was a problem processing your request. Please contact us for assistance.'
									)}
								</span>
							</Alert>
						</div>
					)}
				</div>

				<div className="flex flex-row justify-center w-full whitespace-nowrap">
					<Button type="submit" className={cn('w-full', nextButtonAdditionalClasses)}>
						{t(error && isUserPersistent ? 'Get new code' : isUserPersistent ? 'Next' : 'Sign in')}
					</Button>
				</div>

				{error && (
					<div className="mt-300 flex flex-col items-center text-body2">
						<p>{t('If you are having problems signing in, please call us.')}</p>
						<div className="flex flex-row gap-150">
							{[
								{ label: '+1 (800) 819-5053', href: 'tel:+18008195053' },
								{ label: t('Other Toll-Free Numbers'), href: getTollFreeNumbersLink(fsLocale) },
							].map(({ label, href }) => (
								<Link
									key={href}
									href={href}
									intent="unstyled"
									className="text-body2 underline decoration-from-font underline-offset-2"
								>
									{label}
								</Link>
							))}
						</div>
					</div>
				)}
			</form>
		</div>
	);
}
